/**
 * i18n Configuration
 *
 * Internationalization setup for multilanguage support (English and Urdu).
 *
 * @module lib/i18n
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import enCommon from '@/locales/en/common.json';
import urCommon from '@/locales/ur/common.json';

/**
 * Supported languages
 */
export const languages = {
  en: { name: 'English', nativeName: 'English', dir: 'ltr' },
  ur: { name: 'Urdu', nativeName: 'اردو', dir: 'rtl' },
} as const;

export type Language = keyof typeof languages;

/**
 * Initialize i18next with English and Urdu translations
 */
i18n
  // Detect user language
  .use(LanguageDetector)
  // Pass the i18n instance to react-i18next
  .use(initReactI18next)
  // Initialize i18next
  .init({
    resources: {
      en: {
        common: enCommon,
      },
      ur: {
        common: urCommon,
      },
    },
    defaultNS: 'common',
    fallbackLng: 'en',
    // Don't set lng here - let LanguageDetector handle it from localStorage
    interpolation: {
      escapeValue: false, // React already escapes
    },
    detection: {
      // Order of language detection methods
      order: ['localStorage', 'navigator'],
      // Cache user language preference
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },
  });

// Function to update HTML dir attribute
const updateHtmlDirection = (lng: string) => {
  const language = lng as Language;
  const dir = languages[language]?.dir || 'ltr';
  document.documentElement.dir = dir;
  document.documentElement.lang = lng;
};

// Update HTML dir attribute when language changes
i18n.on('languageChanged', (lng: string) => {
  updateHtmlDirection(lng);
});

// Set initial dir attribute on load
i18n.on('initialized', () => {
  const currentLanguage = i18n.language as Language;
  updateHtmlDirection(currentLanguage);
});

// Also set it immediately (in case initialized already fired)
if (i18n.isInitialized) {
  updateHtmlDirection(i18n.language);
}

export default i18n;